/**
 * i18n setup — English + Polish.
 *
 * Language is auto-detected from device locale on first launch, then
 * persisted via @capacitor/preferences (mirrored to localStorage so the
 * web preview also remembers the choice).
 *
 * Add new keys to BOTH en.json and pl.json. Missing PL keys fall back to EN.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";
import pl from "./locales/pl.json";

const SUPPORTED = ["en", "pl"];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      pl: { translation: pl },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "fitjong_lang",
    },
    returnNull: false,
  });

// Hydrate from Capacitor Preferences (overrides localStorage detection on
// native Android where localStorage isn't available pre-WebView init).
export async function hydrateLanguage() {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: "fitjong_lang" });
    if (value && SUPPORTED.includes(value) && value !== i18n.language) {
      await i18n.changeLanguage(value);
    }
  } catch {
    /* web preview - ignore */
  }
}

export async function setLanguage(lng) {
  if (!SUPPORTED.includes(lng)) return;
  await i18n.changeLanguage(lng);
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem("fitjong_lang", lng);
    }
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: "fitjong_lang", value: lng });
  } catch {
    /* ignore */
  }
}

export const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
];

export default i18n;
